import {useState, useRef, useEffect, useMemo, Fragment} from "react";
import Spinner from "../../../Spinner";
import filterResponse from "../../../../filterResponse";
import Info from "../../../Alerts/Info";
import DropDown from "../Inputs/DropDown";
import Boolean from "../Inputs/Boolean";
import TextField from "../Inputs/TextField";
import RegexField from "../Inputs/RegexField";
import List from "../Inputs/List";
import File from "../Inputs/File";
import { useUploadQaHolds } from "../../../../util/uploadQa";
import useUploadQaWarningsConfirm from "../UploadQaWarnings";
import useCanContinue from "../../../../memos/useCanContinue";
import Error from "../../../Alerts/Error";

export default function RedoEntryModal({entry, editEntry, loading, setLoading}) {
	// A deep copy: the inputs edit the answer objects in place, so sharing them with the entry
	// wrote unsaved edits into the stored answers, and Close had nothing left to go back to.
	const [answers, setAnswers] = useState(() => structuredClone(entry.answers));
	const [otherDisabledReason, setOtherDisabledReason] = useState(false);
	const [uploadQaHeld, setUploadQaHold, uploadQaWarnings] = useUploadQaHolds(); // a file still being checked, or one a blocker metric failed
	const [confirmUploadQaWarnings, uploadQaWarningsDialog] = useUploadQaWarningsConfirm();
	const [uploadsInFlight, setFileLoading] = useState(0); // count, so concurrent uploads each hold the gate
	const fileLoading = uploadsInFlight > 0;
    const [error, setError] = useState("");
	const [hasBeenShown, setHasBeenShown] = useState(false);
	const [formKey, setFormKey] = useState(0); // bumped to remount the fields with the stored answers

	const modalRef = useRef();

	const modalId = `reviewModal-${entry.id}`;
	const modalLabelId = `reviewModalLabel-${entry.id}`;
	const formId = `redoEntryForm-${entry.id}`;

	// The inputs in the body fetch and decode every file of the entry the moment they mount (see
	// Inputs/File/Image.js). One of these modals is rendered per row of the review table, so
	// mounting them with the row downloaded every file on the page before anyone asked for it --
	// and every HEIC among them queued a decode on heic-to's single shared worker, which is what
	// left an image preview spinning until dozens of unrelated decodes had drained.
	//
	// "show" rather than "shown": it fires when the modal starts opening instead of after the fade,
	// so the fields are already loading while it animates in. Once mounted they stay mounted --
	// reopening is then instant, and an upload in flight is never torn down by a close.
	useEffect(() => {
		const modalReference = modalRef.current;

		if (!modalReference) {
			return;
		}

		function onShow() {
			setHasBeenShown(true);
		}

		modalReference.addEventListener("show.bs.modal", onShow);

		return () => {
			modalReference.removeEventListener("show.bs.modal", onShow);
		};
	}, []);

	// Closing without saving discards the edits: the modal stays mounted (above), so without this
	// it reopened still showing values that were never saved. The fields are remounted too, so a
	// file input's preview and upload-QA verdict go back to the stored file. An upload still in
	// flight is left alone rather than torn down.
	const entryAnswersRef = useRef(entry.answers);
	entryAnswersRef.current = entry.answers;
	const uploadsInFlightRef = useRef(0);
	uploadsInFlightRef.current = uploadsInFlight;

	useEffect(() => {
		const modalReference = modalRef.current;

		if (!modalReference) {
			return;
		}

		function onHidden() {
			if (uploadsInFlightRef.current > 0) {
				return;
			}
			setAnswers(structuredClone(entryAnswersRef.current));
			setError("");
			setOtherDisabledReason(false);
			setFormKey(key => key + 1);
		}

		modalReference.addEventListener("hidden.bs.modal", onHidden);

		return () => {
			modalReference.removeEventListener("hidden.bs.modal", onHidden);
		};
	}, []);

	async function onClick(e) {
		if (modalRef.current) {
			const inputs = modalRef.current.querySelectorAll('input[pattern]');
			for (const input of inputs) {
				if (!input.checkValidity()) {
					e.preventDefault();
					input.reportValidity();
					return;
				}
			}
		}

		if (!(await confirmUploadQaWarnings(uploadQaWarnings))) {
			return;
		}

		setLoading(true);

		try {
            setError("");
            const errors = [];
            for (const boolean of answers.booleans) {
                if (!boolean.checked && boolean.requiredTrue) {
                    errors.push(`"${boolean.name}"`);
                }
            }
            if (errors.length) {
                setError(`Please check the following checkboxes before you continue: ${errors.join(", ")}`);
                setLoading(false);
                return;
            }
			const response = await fetch(process.env.REACT_APP_ENDPOINT + '/crowdEntries/' + entry.id + '/update', {
				method: "PUT",
				body: JSON.stringify({
					answers: answers
				}),
				headers: {
					'Content-Type': "application/json"
				},
				credentials: "include"
			});
			await filterResponse(response);
			if (response.status === 200) {
				const {entry} = await response.json();
				editEntry(entry.id, entry);
				// simulate click on cancel button to close the modal
				document.querySelector(`#${modalId} [data-bs-dismiss="modal"]`)?.click();
			}
			else {
				// A row only polls itself while QA is running, so a page left open never learns
				// that QA has accepted the entry since. The server refusing the save is the first
				// the contributor hears of it, and swallowing it would look like a broken button.
				const body = await response.json().catch(() => null);
				setError(body?.error || "This entry could not be saved. Please reload the page and try again.");
			}
		}
		catch (e) {
			console.error(e);
		}
		setLoading(false);
	}

	const inputs = [
		...(entry.answers.booleans || []).map(value => ({ boolean: true, ...value })),
		...(entry.answers.dropDowns || []).map(value => ({ dropDown: true, ...value })),
		...(entry.answers.dropDownsMultiple || []).map(value => ({ dropDown: true, multiple: true, ...value })),
		...(entry.answers.textFields || []).map(value => ({ textField: true, ...value })),
		...(entry.answers.regexFields || []).map(value => ({ regexField: true, ...value })),
		...(entry.answers.lists || []).map(value => ({ list: true, ...value })),
		...(entry.answers.files || []).map(value => ({ file: true, ...value }))
	].sort((a, b) => a.name.localeCompare(b.name));

	const canContinue = useCanContinue(answers);

	const submitDisabled = useMemo(() => {
		return loading || fileLoading || !canContinue || otherDisabledReason || uploadQaHeld;
	}, [loading, canContinue, fileLoading, otherDisabledReason, uploadQaHeld]);

	return (
		<>
			{uploadQaWarningsDialog}
			<div ref={modalRef} className="modal fade" data-bs-backdrop="static" id={modalId} tabIndex="-1" aria-labelledby={modalLabelId} aria-hidden="true">
				<div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg modal-fullscreen-sm-down">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title" id={modalLabelId}>Edit entry</h5>
							<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div className="modal-body input-sections" onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.stopPropagation();
							}
						}}>
                            {error && <Error message={error} />}
							{entry.instruction && <Info message={entry.instruction} /> }
							{hasBeenShown && <Fragment key={formKey}>{inputs.map(value => {
								if (value.dropDown) {
									return <DropDown formId={formId} dropDown={value} answers={answers} setAnswers={setAnswers} key={value.id}/>;
								}
								if (value.boolean) {
									return <Boolean formId={formId} boolean={value} answers={answers} setAnswers={setAnswers} key={value.id}/>
								}
								if (value.textField) {
									return <TextField formId={formId} textField={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
								}
								if (value.regexField) {
									return <RegexField formId={formId} regexField={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
								}
								if (value.list) {
									return <List formId={formId} list={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
								}
								if (value.file) {
									return <File formId={formId} setOtherDisabledReason={setOtherDisabledReason} setUploadQaHold={setUploadQaHold} setFileLoading={setFileLoading} file={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
								}
								return  null;
							})}</Fragment>}
						</div>
						<div className="modal-footer">
							<div className="d-flex gap-3">
								<button type="button" className="btn btn-success" onClick={onClick} disabled={submitDisabled}>{(loading || fileLoading) && <Spinner small={true} />} {fileLoading ? `Uploading${uploadsInFlight > 1 ? ` ${uploadsInFlight} files` : ''}…` : 'Save'}</button>
								<button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
